import { useCallback, useMemo } from "react"
import type { OperationContext, OperationResult } from "urql"
import { useClient, useMutation, useQuery } from "urql"

import type {
  CompanyListSuccess,
  CompanyMutationCreateArgs,
  CompanyMutationUpdateArgs,
  CompanyTypeTreeSuccess,
  Mutation,
  Query,
} from "@/graphql/codegen/graphql"
import {
  CompanyBaseListDocument,
  CompanyTypeTreeDocument,
  CreateNewCompanyMutationDocument,
  UpdateNewCompanyMutationDocument,
} from "@/graphql/codegen/graphql"
import { isCompanyDuplicate } from "@/screens/Companies/utils"

export const useCreateNewCompanyModal = (): {
  companyTypeFetching: boolean
  companyTypeList: "JSON" | any[]
  checkCompanyNameDuplicate: (name: string, excludeCompanyId?: string) => Promise<boolean>
  updateCompany: (
    variables: CompanyMutationUpdateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "company">, CompanyMutationUpdateArgs>>
  createCompany: (
    variables: CompanyMutationCreateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "company">, CompanyMutationCreateArgs>>
} => {
  const client = useClient()

  // Query for company type list
  const [{ data: companyTypeData, fetching: companyTypeFetching }] = useQuery<Pick<Query, "companyType">>({
    query: CompanyTypeTreeDocument,
  })

  // Mutation to create a new company
  const [, createCompany] = useMutation<Pick<Mutation, "company">, CompanyMutationCreateArgs>(
    CreateNewCompanyMutationDocument
  )

  // Mutation to update a company
  const [, updateCompany] = useMutation<Pick<Mutation, "company">, CompanyMutationUpdateArgs>(
    UpdateNewCompanyMutationDocument
  )

  // Memoize the company type list
  const companyTypeList = useMemo(() => {
    return (companyTypeData?.companyType?.tree as CompanyTypeTreeSuccess)?.tree || []
  }, [companyTypeData])

  const checkCompanyNameDuplicate = useCallback(
    async (name: string, excludeCompanyId?: string): Promise<boolean> => {
      if (name.length < 3) return false
      const result = await client
        .query<
          Pick<Query, "company">
        >(CompanyBaseListDocument, { input: { filter: { query: name }, limit: 50, offset: 0 } }, { requestPolicy: "network-only" })
        .toPromise()
      const companies = ((result.data?.company.list as CompanyListSuccess)?.companies || []).map((company) => ({
        companyId: company.companyId ?? "",
        name: company.name ?? "",
      }))
      return isCompanyDuplicate(companies, name, excludeCompanyId)
    },
    [client]
  )

  return {
    companyTypeList,
    companyTypeFetching,
    createCompany,
    updateCompany,
    checkCompanyNameDuplicate,
  }
}
